import {
  Alert,
  Col,
  COLOR,
  Icon,
  Inline,
  LabeledList,
  LabeledListItem,
  Paragraph,
  Row,
} from "@george-labs.com/design-system"
import dayjs from "../../utils/dayjs"
import { ReactElement, useState } from "react"
import {
  AccountNumber,
  AmountComponent,
  Cvt,
  DecimalComponent,
} from "../../Shared"
import PhoneNumber from "../PhoneNumber/PhoneNumber"
import YesNo from "../YesNo/YesNo"
import MultiCvt from "../CVT/MultiCvt"
import { ICON } from "@george-labs.com/george-assets/icons"

type FieldType =
  | "text"
  | "number"
  | "date"
  | "decimal"
  | "CVT"
  | "MultiCVT"
  | "bankAccount"
  | "phone"
  | "yesno"
  | ReactElement

export interface IField {
  label: string
  value: string | number | boolean | Date | ReactElement | null
  fieldType: FieldType
  appendText?: string
  noWhiteSpaceAppendText?: string
  prependText?: string
  list?: string
  lists?: string[]
  hidden?: boolean
  icon?: ReactElement
  info?: ReactElement
}
interface IDataListProps {
  dataList: Array<IField>
  noResultText?: string
  displayAsLabeledList?: boolean
  isStripedLabeledList?: boolean
}

const DataList = (props: IDataListProps) => {
  const [infos, setInfos] = useState<number[]>([])

  const toggleInfo = (idx: number): void => {
    if (infos.includes(idx)) {
      setInfos(infos.filter((el) => el !== idx))
    } else {
      setInfos([...infos, idx])
    }
  }

  const getConvertedValue = (field: IField) => {
    switch (field.fieldType) {
      case "text":
        return (
          <>
            {field.icon ? field.icon : ""}
            {field.prependText ? ` ${field.prependText}` : ""}
            {field.value}
            {field.appendText ? ` ${field.appendText}` : ""}
          </>
        )
      case "number":
        return (
          <AmountComponent
            value={field.value as string | number}
            appendText={field.appendText}
          ></AmountComponent>
        )
      case "decimal":
        return (
          <DecimalComponent
            value={field.value as string | number}
            appendText={field.appendText}
          ></DecimalComponent>
        )
      case "CVT":
        return (
          <Cvt
            list={field.list}
            code={field.value as string}
            appendText={field.appendText}
            noWhiteSpaceAppendText={field.noWhiteSpaceAppendText}
            prependText={field.prependText}
          />
        )
      case "MultiCVT":
        return <MultiCvt lists={field.lists} code={field.value as string} />
      case "date":
        return <>{dayjs(field.value as string | Date).format("LL")}</>
      case "bankAccount":
        return <AccountNumber value={field.value as string}></AccountNumber>
      case "phone":
        return <PhoneNumber value={field.value as string}></PhoneNumber>
      case "yesno":
        return <YesNo value={field.value as boolean}></YesNo>
    }
  }

  const values = props.dataList.filter((field) => field?.value !== null)

  if (props.displayAsLabeledList) {
    return (
      <LabeledList
        alignment={LabeledList.ALIGNMENT.RIGHT}
        labelSize={LabeledList.LABEL_SIZE.SMALL}
        variant={
          props.isStripedLabeledList
            ? LabeledList.VARIANT.STRIPED
            : LabeledList.VARIANT.PLAIN
        }
      >
        {values.map((field: IField, index: number) => {
          return (
            <span key={`${field.label}-${index}`}>
              <LabeledListItem label={field.label}>
                {getConvertedValue(field)}
                {field.info && (
                  <Inline className="ml-1">
                    <Icon
                      assetIcon={ICON.INFO}
                      color={COLOR.INFO}
                      onClick={() => toggleInfo(index)}
                      style={{
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    />
                  </Inline>
                )}
              </LabeledListItem>
              {infos.includes(index) && (
                <span
                  style={{
                    paddingLeft: "6px",
                    paddingRight: "6px",
                    display: "block",
                  }}
                  key={`${field.label}-info-${index}`}
                >
                  <Alert variant={Alert.VARIANT.INFO}>{field.info}</Alert>
                </span>
              )}
            </span>
          )
        })}
      </LabeledList>
    )
  }

  return (
    <>
      {!!values.length &&
        values.map((field: IField, index: number) => {
          return (
            <Row className="mb-2" key={`${field.label}-${index}`}>
              <Col md={6}>
                <Paragraph>
                  <Inline emphasis>{field.label}</Inline>
                </Paragraph>
              </Col>
              <Col md={6}>
                <Paragraph>
                  <Inline>{getConvertedValue(field)}</Inline>
                  {field.info && (
                    <Inline className="ml-1">
                      <Icon
                        assetIcon={ICON.INFO}
                        color={COLOR.INFO}
                        onClick={() => toggleInfo(index)}
                        style={{
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      />
                    </Inline>
                  )}
                </Paragraph>
                {infos.includes(index) && (
                  <Alert variant={Alert.VARIANT.INFO}>{field.info}</Alert>
                )}
              </Col>
            </Row>
          )
        })}
      {!values.length && props.noResultText && (
        <Paragraph className="text-center g-fg-ui-yellow p-3">
          {props.noResultText}
        </Paragraph>
      )}
    </>
  )
}

export default DataList
